import { useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { authService } from '@/services/authService'
import { SignupScreenDesktop } from './SignupScreenDesktop'
import { SignupScreenMobile } from './SignupScreenMobile'
import { getDefaultSignupFormData, type SignupScreenBaseProps } from './SignupScreenBase'

/**
 * SignupScreen component
 * Renders mobile or desktop version based on screen size
 * 
 * Uses component split pattern for different layouts:
 * - Mobile: Compact layout with vertical spacing
 * - Desktop: Standard layout with horizontal spacing
 * 
 * Manages form state, validation, password strength, and submission logic
 */
const SignupScreen = () => {
  const isMobile = useIsMobile()
  const [formData, setFormData] = useState(getDefaultSignupFormData())
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const clearField = (field: 'email' | 'password' | 'confirmPassword') => {
    setFormData(prev => ({ ...prev, [field]: '' }))
  }

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return false
    }

    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      const { confirmPassword, ...signupData } = formData
      await authService.signup(signupData)
      // After successful signup, redirect to attestation
      window.location.href = '/attestation'
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const baseProps: SignupScreenBaseProps = {
    formData,
    showPassword,
    showConfirmPassword,
    acceptTerms,
    loading,
    error,
    onFormDataChange: handleFormDataChange,
    onShowPasswordChange: setShowPassword,
    onShowConfirmPasswordChange: setShowConfirmPassword,
    onAcceptTermsChange: setAcceptTerms,
    onClearField: clearField,
    onSubmit: handleSubmit,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <SignupScreenMobile {...baseProps} />
  }

  return <SignupScreenDesktop {...baseProps} />
}

export default SignupScreen

